import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicLayout from '../../components/PublicLayout';
import { Home, ArrowLeft, Mail } from 'lucide-react';

const SimulationMaison: React.FC = () => {
    const navigate = useNavigate();

    const PRIX_PUISSANCE = { '3.7': 800, '7.4': 1200, '11': 1600, '22': 2000 };
    const PRIX_INSTALL_BASE = 300;
    const PRIX_PAR_METRE = 50;
    const PRIX_OPTION = { cable: 200, connectee: 150, heures_creuses: 100 };
    const CREDIT_IMPOT = 300;

    const [formState, setFormState] = useState(() => {
        const savedData = localStorage.getItem('maisonFormData');
        if (savedData) {
            return JSON.parse(savedData);
        } else {
            return {
                logement: 'maison',
                distance: 10,
                puissance: 3.7,
                installation: 'monophase',
                vehicule: '',
                cable: false,
                connectee: false,
                heures_creuses: false,
            };
        }
    });

    const calculation = useMemo(() => {
        let borneCost = PRIX_PUISSANCE[formState.puissance];
        let installCost = PRIX_INSTALL_BASE + (formState.distance * PRIX_PAR_METRE);
        let optionsCost = 0;
        if (formState.cable) optionsCost += PRIX_OPTION.cable;
        if (formState.connectee) optionsCost += PRIX_OPTION.connectee;
        if (formState.heures_creuses) optionsCost += PRIX_OPTION.heures_creuses;
        
        const totalBrut = borneCost + installCost + optionsCost;
        const totalNet = totalBrut - CREDIT_IMPOT;

        return {
            borneCost,
            installCost,
            optionsCost,
            totalBrut,
            totalNet
        };
    }, [formState]);

    useEffect(() => {
        localStorage.setItem('maisonFormData', JSON.stringify(formState));
    }, [formState]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        if (type === 'checkbox') {
            const { checked } = e.target as HTMLInputElement;
            setFormState(prev => ({ ...prev, [name]: checked }));
        } else {
            setFormState(prev => ({ ...prev, [name]: value }));
        }
    };
    
    const sendMail = () => {
        const subject = "Demande de devis via le simulateur - Installation à Domicile";
        const body = `
Bonjour,

Voici le résumé d'une simulation effectuée sur le site ELECBORNE :

ESTIMATION TOTALE : ${calculation.totalNet.toFixed(2)} €

DÉTAILS DE LA DEMANDE :
---------------------------------
Type de logement : ${formState.logement}
Distance compteur-parking : ${formState.distance}m
Puissance de borne souhaitée : ${formState.puissance} kW
Type d'installation électrique : ${formState.installation}
Véhicule : ${formState.vehicule || 'Non précisé'}

OPTIONS SOUHAITÉES :
---------------------------------
Câble Type 2 attaché : ${formState.cable ? 'Oui' : 'Non'}
Borne connectée : ${formState.connectee ? 'Oui' : 'Non'}
Programmation heures creuses : ${formState.heures_creuses ? 'Oui' : 'Non'}

Merci de bien vouloir me recontacter pour affiner ce devis.

Cordialement,
[Votre Nom]
        `.trim().replace(/^ +/gm, '');

        window.location.href = `mailto:contact@elecborne.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    };

    return (
        <PublicLayout>
            <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
                <button onClick={() => navigate('/simulateur')} className="flex items-center text-gray-600 hover:text-primary-600 mb-6">
                    <ArrowLeft className="w-5 h-5 mr-2" />
                    Retour aux simulateurs
                </button>
                <div className="bg-white p-8 rounded-2xl shadow-lg">
                    <div className="text-center mb-8">
                        <Home className="mx-auto w-12 h-12 text-primary-600" />
                        <h1 className="text-3xl font-bold text-gray-900 mt-4">Simulateur IRVE - Installation à Domicile</h1>
                    </div>

                    <div className="grid md:grid-cols-2 gap-12">
                        {/* Formulaire */}
                        <div className="space-y-6">
                            {/* Fields here */}
                            <div>
                                <label className="font-semibold">Type de logement</label>
                                <select name="logement" value={formState.logement} onChange={handleInputChange} className="mt-2 w-full p-3 border rounded-lg"> 
                                    <option value="maison">Maison</option>
                                    <option value="appartement">Appartement</option>
                                </select>
                            </div>
                            <div>
                                <label className="font-semibold">Distance compteur-parking</label>
                                <select name="distance" value={formState.distance} onChange={handleInputChange} className="mt-2 w-full p-3 border rounded-lg">
                                    <option value="10">&lt;10m</option>
                                    <option value="15">10-20m</option>
                                    <option value="25">&gt;20m</option>
                                </select>
                            </div>
                            <div>
                                <label className="font-semibold">Puissance de la borne</label>
                                <div className="mt-2 grid grid-cols-2 gap-2">
                                    {Object.keys(PRIX_PUISSANCE).map(p => (
                                        <button key={p} onClick={() => setFormState(prev => ({...prev, puissance: p}))} className={`p-3 rounded-lg text-center ${formState.puissance == p ? 'bg-primary-600 text-white' : 'bg-gray-100'}`}>{p} kW</button>
                                    ))}
                                </div>
                            </div>
                             <div>
                                <label className="font-semibold">Type d'installation électrique</label>
                                <select name="installation" value={formState.installation} onChange={handleInputChange} className="mt-2 w-full p-3 border rounded-lg">
                                    <option value="monophase">Monophasé</option>
                                    <option value="triphase">Triphasé</option>
                                    <option value="nsp">Je ne sais pas</option>
                                </select>
                            </div>
                            <div>
                                <label className="font-semibold">Marque et modèle du véhicule</label>
                                <input type="text" name="vehicule" value={formState.vehicule} onChange={handleInputChange} placeholder="Ex: Renault Zoe" className="mt-2 w-full p-3 border rounded-lg" />
                            </div>
                            <div>
                                <label className="font-semibold">Options</label>
                                <div className="mt-2 space-y-2">
                                    <div className="flex items-center"><input type="checkbox" name="cable" checked={formState.cable} onChange={handleInputChange} className="h-5 w-5 rounded"/><label className="ml-3">Câble Type 2 attaché</label></div>
                                    <div className="flex items-center"><input type="checkbox" name="connectee" checked={formState.connectee} onChange={handleInputChange} className="h-5 w-5 rounded"/><label className="ml-3">Borne connectée (WiFi/4G)</label></div>
                                    <div className="flex items-center"><input type="checkbox" name="heures_creuses" checked={formState.heures_creuses} onChange={handleInputChange} className="h-5 w-5 rounded"/><label className="ml-3">Programmation heures creuses</label></div>
                                </div>
                            </div>
                        </div>

                        {/* Resultat */}
                        <div className="bg-green-50 p-6 rounded-xl border-l-4 border-green-500">
                            <h2 className="text-2xl font-bold text-green-800 mb-4">Votre Estimation</h2>
                            <div className="space-y-3 text-gray-700">
                                <div className="flex justify-between"><span>Borne</span><span>{calculation.borneCost.toFixed(2)} €</span></div>
                                <div className="flex justify-between"><span>Installation</span><span>{calculation.installCost.toFixed(2)} €</span></div>
                                {formState.cable && <div className="flex justify-between"><span>Option: Câble</span><span>{PRIX_OPTION.cable.toFixed(2)} €</span></div>}
                                {formState.connectee && <div className="flex justify-between"><span>Option: Connectée</span><span>{PRIX_OPTION.connectee.toFixed(2)} €</span></div>}
                                {formState.heures_creuses && <div className="flex justify-between"><span>Option: Programmation</span><span>{PRIX_OPTION.heures_creuses.toFixed(2)} €</span></div>}
                                <hr className="my-3"/>
                                <div className="flex justify-between font-bold"><span>Sous-total</span><span>{calculation.totalBrut.toFixed(2)} €</span></div>
                                <div className="flex justify-between"><span>Crédit d'impôt</span><span className="text-green-600">- {CREDIT_IMPOT.toFixed(2)} €</span></div>
                                <hr className="my-3"/>
                                <p className="text-center text-lg font-semibold">TOTAL NET ESTIMÉ</p>
                                <p className="text-center text-4xl font-bold text-green-700">{calculation.totalNet.toFixed(2)} €</p>
                            </div>
                            <button onClick={sendMail} className="mt-6 w-full bg-primary-600 text-white font-bold py-3 rounded-lg flex items-center justify-center hover:bg-primary-700">
                                <Mail className="w-5 h-5 mr-2" /> Envoyer mon devis
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </PublicLayout>
    );
};

export default SimulationMaison;
